import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import block from 'bem-cn';

import SVG from 'components/SVG';
import sortSVG from 'features/reports/img/sort.svg';
import TableRow from './TableRow/TableRow';

import './TableProviders.scss';

const b = block('reports-table-providers');
const headers = ['provider', 'betCount', 'betAmount', 'winAmount', 'incomeAmount', 'rtp', 'activeUsers'];

const parseEuropeanNumber = value => {
  if (typeof value === 'number') return value;
  if (!value || typeof value !== 'string') return 0;
  return Number(value.replace(/\./g, '').replace(',', '.')) || 0;
};

const TableProviders = ({ providers, values, handlers, withDiff, currency }) => {
  const locale = useSelector(state => state.locale.locale);
  const { providersDiff } = useSelector(state => state.reports.statisticProvider);
  const providerRanks = useSelector(state => state.reports.providerRanks);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const sortedProviders = useMemo(() => {
    if (!sortConfig.key) return providers;
    return [...providers].sort((first, second) => {
      const firstValue = sortConfig.key === 'provider'
        ? first.provider.toLowerCase()
        : parseEuropeanNumber(sortConfig.key === 'activeUsers'
          ? values.activeUsers.data?.providers.find(item => item.providerKey === first.providerKey)?.activeUsers
          : first[sortConfig.key]);
      const secondValue = sortConfig.key === 'provider'
        ? second.provider.toLowerCase()
        : parseEuropeanNumber(sortConfig.key === 'activeUsers'
          ? values.activeUsers.data?.providers.find(item => item.providerKey === second.providerKey)?.activeUsers
          : second[sortConfig.key]);
      if (firstValue === secondValue) return 0;
      const direction = firstValue > secondValue ? 1 : -1;
      return sortConfig.direction === 'asc' ? direction : -direction;
    });
  }, [providers, sortConfig, values.activeUsers.data]);

  const toggleSort = useCallback(key => {
    setSortConfig(previous => ({
      key,
      direction: previous.key === key && previous.direction === 'desc' ? 'asc' : 'desc',
    }));
  }, []);

  return (
    <section className={b()}>
      <div className={b('header')}>{locale.byProvider}</div>
      <div className={b('table')}>
        {providers.length ? (
          <>
            <div className={b('table-header')}>
              {headers.map(head => (
                <button
                  type="button"
                  key={head}
                  className={b('table-head')}
                  onClick={() => toggleSort(head)}
                  disabled={head === 'activeUsers' && (
                    !values.expandedProvider || !values.activeUsers.data || values.activeUsers.isLoading
                  )}>
                  {locale[head]}
                  <SVG
                    svgProps={{ svg: sortSVG }}
                    className={b('table-head-icon', {
                      rotate: sortConfig.direction === 'asc' && sortConfig.key === head,
                      inactive: sortConfig.key !== head,
                    })}
                  />
                </button>
              ))}
            </div>
            <div className={b('table-body')}>
              {sortedProviders.map(item => (
                <TableRow
                  key={item.providerKey}
                  item={item}
                  activeUsers={values.expandedProvider ? values.activeUsers.data : null}
                  isActiveUsersLoading={values.expandedProvider === item.providerKey && values.activeUsers.isLoading}
                  isToggleDisabled={values.activeUsers.isLoading}
                  diff={providersDiff?.[item.providerKey]}
                  isExpanded={values.expandedProvider === item.providerKey}
                  onToggle={() => handlers.onProviderToggle(item.providerKey)}
                  selectedMetrics={values.providerMetrics}
                  onMetricsApply={handlers.onProviderMetricsApply}
                  onScheduleOpen={handlers.onProviderScheduleOpen}
                  metricError={values.providerMetricError}
                  ranks={providerRanks}
                  withDiff={withDiff}
                  currency={currency}
                />
              ))}
            </div>
          </>
        ) : (
          <div className={b('no-results')}>{locale.noResults}</div>
        )}
      </div>
    </section>
  );
};

export default TableProviders;
